import { useState } from 'react';
import { updateGame } from '../../services/content/gameService';
import type { Game, ScoreMeter, ScoreTier } from '../../types/content/game';

type View = 'list' | 'detail';

interface GameScoreMeterModalProps {
  game: Game;
  onClose: () => void;
  onSuccess: () => void;
}

const emptyMeter: ScoreMeter = { label: '', tiers: [] };

export function GameScoreMeterModal({ game, onClose, onSuccess }: GameScoreMeterModalProps) {
  const initial = game.scoreMeter || emptyMeter;
  const [label, setLabel] = useState(initial.label);
  const [tiers, setTiers] = useState<ScoreTier[]>(JSON.parse(JSON.stringify(initial.tiers || [])));
  const [view, setView] = useState<View>('list');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [tierRange, setTierRange] = useState('');
  const [tierDesc, setTierDesc] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const btnOutline = 'px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors';
  const btnPrimary = 'px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';
  const btnSmall = 'text-xs px-2 py-1 rounded transition-colors';

  function openForm(index: number | null) {
    if (index !== null) {
      setTierRange(tiers[index].range);
      setTierDesc(tiers[index].description);
    } else {
      setTierRange('');
      setTierDesc('');
    }
    setEditingIndex(index);
    setView('detail');
  }

  function saveTier() {
    if (!tierRange.trim()) return;
    const updated = [...tiers];
    const item: ScoreTier = { range: tierRange.trim(), description: tierDesc.trim() };
    if (editingIndex !== null) {
      updated[editingIndex] = item;
    } else {
      updated.push(item);
    }
    setTiers(updated);
    setView('list');
  }

  function deleteTier(index: number) {
    setTiers(tiers.filter((_, i) => i !== index));
  }

  async function handleSave() {
    setError('');
    setLoading(true);
    try {
      const meter: ScoreMeter = { label: label.trim(), tiers };
      await updateGame(game.id, { ...game, scoreMeter: meter });
      onSuccess();
    } catch {
      setError('Failed to update score meter');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        {view === 'list' && (
          <>
            <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Score Meter</h2>
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Label</label>
                  <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} className={inputClass} placeholder="Score meter label" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className={labelClass}>Tiers ({tiers.length})</label>
                    <button type="button" onClick={() => openForm(null)} className={`${btnSmall} text-blue-600 hover:bg-blue-50`}>+ Add Tier</button>
                  </div>
                  {tiers.length === 0 ? (
                    <p className="text-sm text-gray-400">No tiers yet</p>
                  ) : (
                    <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                      {tiers.map((t, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-2">
                          <div className="min-w-0">
                            <span className="text-sm font-medium text-gray-700">{t.range}</span>
                            {t.description && <span className="text-sm text-gray-500 ml-2">— {t.description}</span>}
                          </div>
                          <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                            <button type="button" onClick={() => openForm(i)} className={`${btnSmall} text-gray-500 hover:bg-gray-100`}>Edit</button>
                            <button type="button" onClick={() => deleteTier(i)} className={`${btnSmall} text-red-500 hover:bg-red-50`}>Delete</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button type="button" onClick={onClose} className={btnOutline}>Cancel</button>
              <button type="button" onClick={handleSave} disabled={loading} className={btnPrimary}>{loading ? 'Saving...' : 'Save'}</button>
            </div>
          </>
        )}
        {view === 'detail' && (
          <>
            <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
              <h2 className="text-lg font-bold text-gray-900 mb-4">{editingIndex !== null ? 'Edit Tier' : 'Add Tier'}</h2>
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Range *</label>
                  <input type="text" value={tierRange} onChange={(e) => setTierRange(e.target.value)} className={inputClass} placeholder="e.g. 80-100" />
                </div>
                <div>
                  <label className={labelClass}>Description</label>
                  <input type="text" value={tierDesc} onChange={(e) => setTierDesc(e.target.value)} className={inputClass} placeholder="Tier description" />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button type="button" onClick={() => setView('list')} className={btnOutline}>Back</button>
              <button type="button" onClick={saveTier} disabled={!tierRange.trim()} className={btnPrimary}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
